import { useTheme } from '@mui/material/styles'
import * as turf from '@turf/turf'
import { debounce } from 'es-toolkit'
import type { DataDrivenPropertyValueSpecification, MapMouseEvent } from 'mapbox-gl'
import { LngLat, LngLatBounds } from 'mapbox-gl'
import { useEffect, useMemo } from 'react'
import { Source } from 'react-map-gl/mapbox'
import { useActiveOnTimelineCombinedMostSevereAlertState, useCurrentMostSevereAlertState } from '../../data/alerts/useAlerts.ts'
import { useDeviceCombinedStatuses } from '../../data/devices.ts'
import type { Location } from '../../data/locations.tsx'
import { useMeasurementAlertsRecord } from '../../data/measurementAlerts.ts'
import { useMap, useRenderedLocations } from '../../states.ts'
import { useMapTheme } from '../../theme/mapPalette.ts'
import { OrderedLayer } from '../layers/customLayers.tsx'
import { useDistanceTool } from '../map/DistanceTool.tsx'
import { fitBounds } from '../map/mapUtils.ts'
import { useTimeline } from '../timeline/timelineState.ts'

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO
export const LocationLayer = ({
	locations,
	onMapClick,
}: {
	locations: Location[]
	onMapClick?: (location: { lat: number; lon: number }) => void
}) => {
	const theme = useTheme()
	const mapTheme = useMapTheme()
	const fgColor = mapTheme.mapMarker.fg
	const bgColor = mapTheme.mapMarker.bg
	const map = useMap((s) => s.map)
	const addRenderedLocation = useRenderedLocations((s) => s.add)
	const deviceStatuses = useDeviceCombinedStatuses()
	const measurementAlerts = useMeasurementAlertsRecord()
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const isDistanceToolEnabled = useDistanceTool((s) => s.enabled)

	useEffect(() => {
		if (!map) return

		const onMouseEnterCluster = () => {
			map.getCanvas().style.cursor = 'pointer'
		}
		const onMouseLeaveCluster = () => {
			map.getCanvas().style.cursor = 'crosshair'
		}
		const onClickFn = debounce(async (e: MapMouseEvent) => {
			if (isDistanceToolEnabled) return

			const firstCluster = map.queryRenderedFeatures(e.point, { layers: [clusterLayerId] }).at(0)
			if (firstCluster) {
				const coords = parseCoords(firstCluster.properties?.coords)
				const bounds = coords.reduce((acc, cur) => acc.extend(new LngLat(cur.lon, cur.lat)), new LngLatBounds())
				fitBounds(map, bounds, { padding: Math.min(200, globalThis.innerWidth * 0.1, globalThis.innerHeight * 0.1) })
				return
			}

			if (onMapClick) {
				const wrapped = e.lngLat.wrap()
				const lat = Number(wrapped.lat.toFixed(3))
				const lon = Number(wrapped.lng.toFixed(3))
				onMapClick({ lat, lon })
			}
		}, 300)
		const onClick = (e: MapMouseEvent) => onClickFn(e)
		const onDblClick = () => onClickFn.cancel()

		const onMouseEnterLocation = (e: MapMouseEvent) => {
			if (isDistanceToolEnabled) return
			const location = e.features?.find((f) => f.properties?.locationId)
			if (!location) return
			const locationId = location.properties?.locationId
			if (typeof locationId !== 'string') return
			addRenderedLocation(locationId)
		}
		map.on('mouseenter', clusterLayerId, onMouseEnterCluster)
		map.on('mouseleave', clusterLayerId, onMouseLeaveCluster)
		map.on('click', onClick)
		map.on('dblclick', onDblClick)
		map.on('mouseenter', layerId, onMouseEnterLocation)
		return () => {
			map.removeInteraction('cluster-click')
			map.off('mouseenter', clusterLayerId, onMouseEnterCluster)
			map.off('mouseleave', clusterLayerId, onMouseLeaveCluster)
			map.off('click', onClick)
			map.off('dblclick', onDblClick)
			map.off('mouseenter', layerId, onMouseEnterLocation)
		}
	}, [addRenderedLocation, map, onMapClick, isDistanceToolEnabled])

	const currentAssetState = useCurrentMostSevereAlertState()
	const timelineAssetState = useActiveOnTimelineCombinedMostSevereAlertState()

	const geojson = useMemo(
		() =>
			turf.featureCollection(
				locations.map((l) => {
					const deviceStatus = l.type === 'device' ? (l.status ?? deviceStatuses[l.id]?.status) : undefined
					const badDeviceStatus = deviceStatus === 'ERROR' || deviceStatus === 'OFFLINE'
					const hasActiveMeasurementAlerts = l.type === 'device' && !!measurementAlerts[l.id]?.active.length
					const assetState = l.type === 'asset' ? (isNowLocked ? currentAssetState : timelineAssetState)[l.assetVrn] : undefined
					const severity = assetState?.severity ?? 'clear'
					return turf.point([l.lon, l.lat], {
						locationId: l.id,
						coords: `${l.lon},${l.lat};`,
						icon: l.icon, // These icons are defined in the mapbox styles
						severity: severityLevels[hasActiveMeasurementAlerts ? 'alarm' : badDeviceStatus ? 'badStatus' : severity ? severity : 'clear'],
					})
				}),
			),
		[deviceStatuses, locations, measurementAlerts, currentAssetState, isNowLocked, timelineAssetState],
	)

	const severityColorMap: DataDrivenPropertyValueSpecification<string> = [
		'case',
		['==', ['get', 'severity'], severityLevels.alarm],
		theme.alerts.color.alarm,
		['==', ['get', 'severity'], severityLevels.warning],
		theme.alerts.color.warning,
		['==', ['get', 'severity'], severityLevels.info],
		theme.alerts.color.info,
		['==', ['get', 'severity'], severityLevels.badStatus],
		theme.palette.devices.badStatus,
		bgColor,
	]

	return (
		<Source
			type='geojson'
			data={geojson}
			cluster
			clusterRadius={20}
			clusterProperties={{ coords: ['concat', ['get', 'coords']], severity: ['max', ['get', 'severity']] }}
		>
			<OrderedLayer
				id={layerId}
				type='circle'
				filter={['!', ['has', 'point_count']]}
				paint={{ 'circle-radius': 12, 'circle-color': severityColorMap, 'circle-stroke-width': 1, 'circle-stroke-color': fgColor }}
			/>
			<OrderedLayer
				id={`${layerId}-icon`}
				type='symbol'
				filter={['!', ['has', 'point_count']]}
				layout={{ 'icon-image': ['get', 'icon'], 'icon-allow-overlap': true, 'icon-size': 1.8 }}
			/>
			<OrderedLayer
				id={clusterLayerId}
				type='circle'
				filter={['has', 'point_count']}
				paint={{ 'circle-radius': 16, 'circle-color': severityColorMap, 'circle-stroke-width': 2, 'circle-stroke-color': fgColor }}
			/>
			<OrderedLayer
				id={`${layerId}-count`}
				type='symbol'
				filter={['has', 'point_count']}
				paint={{ 'text-color': fgColor, 'text-halo-color': fgColor, 'text-halo-width': 0.5 }}
				layout={{ 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 16, 'text-allow-overlap': true }}
			/>
		</Source>
	)
}

const layerId = 'locations'
const clusterLayerId = `${layerId}-cluster`
const severityLevels = { alarm: 4, warning: 3, info: 2, badStatus: 1, clear: 0 } as const

const parseCoords = (coords: unknown) => {
	if (typeof coords !== 'string') return []
	return coords.split(';').flatMap((c) => {
		const [lon, lat] = c.split(',').map(Number)
		if (lon == null || lat == null) return []
		return [{ lon, lat }]
	})
}
